import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import Modal from "./Modal";
import '../style/LoginModal.css';
import constants from "../helpers/constants";
import { IoCloseCircle } from "react-icons/io5";
import { setCookieBase64 } from "./setCookieBase64";
import { getCookieBase64 } from "./getCookieBase64";
import getApiHeaders from '../helpers/apiHeaders.js';
import Logo from './Logo';

const LoginModal = ({ notification, resetPasswordToken, topic }) => {
    const { t } = useTranslation();
    const [auth, setAuth] = useState()
    const [isLogin, setIsLogin] = useState(true)
    const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)
    const [formData, setFormData] = useState({ email: "", password: "" })
    const [fetching, setFetching] = useState(false);
    const [actionUrl, setActionUrl] = useState("");
    const emailRef = useRef(null);

    useEffect(() => {
        const recoverSession = () => {
            try {
                const cookie = getCookieBase64('auth')

                if (cookie) {
                    setAuth(cookie);
                }
            } catch (err) {
                console.error("Error decoding session:", err);
                setAuth(null);
            }
        };

        recoverSession();
    }, []);

    const toggleMode = () => {
        if (fetching) { return }
        setIsLogin((prevMode) => !prevMode);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleLogout = async () => {
        await fetch(constants.fetchAuthLogout, {
            method: "POST",
            headers: getApiHeaders(),
            credentials: "include"
        });

        document.cookie = "auth=; path=/; max-age=0";
        window.location.reload();
    };

    const handleSubmit = async () => {
        if (fetching) { return }

        var endpoint = isLogin ? constants.fetchLogin : constants.fetchRegister
        if (isPasswordRecovery) {
            endpoint = constants.fetchPasswordRecovery
        }

        const method = "POST";
        const headers = getApiHeaders();

        try {
            setFetching(true)

            const response = await fetch(endpoint, {
                method,
                headers,
                body: JSON.stringify(formData),
                credentials: "include"
            });

            const result = await response.json();

            const statusMessages = {
                200: t('auth.loginSuccess'),
                401: t('auth.loginFailedPassword'),
                403: t('auth.loginFailedEmail'),
            };

            const title = result.message

            const message = title === "Login successful" ? statusMessages[response.status] || t('auth.unexpectedError')
                : "";

            const tag = response.status === 200 ? t('common.phew') : t('common.ops');
            if (result.message) {
                notification.open({
                    content: (
                        <div>
                            <div className="d-flex justify-content-between px-2 mb-2">
                                <small className="tag">{tag}</small>
                                <strong>{title}</strong>
                            </div>
                            <div>{message}</div>
                        </div>
                    ),
                    duration: 30,
                    closable: true,
                    showProgress: false,
                    closeIcon: <IoCloseCircle />,
                });
            }

            if (response.ok) {
                if (isLogin && !isPasswordRecovery) {
                    setCookieBase64("auth", result, { days: 1 })
                }
            } else {
                console.error(result.error || "Something went wrong. Please try again.");
            }

            document.dispatchEvent(new CustomEvent('closeModal'));
        } catch (error) {
            console.error("Network error. Please try again.");
        } finally {
            setFetching(false)
        }
    };

    function getButtonText() {
        if (fetching) return t('common.processing');
        if (resetPasswordToken) return t('common.confirm');
        if (isPasswordRecovery) return t('auth.sendEmailForRecovery');
        if (isLogin) return t('auth.login');
        return t('auth.register');
    }

    useEffect(() => {
        if (!auth || auth.access !== "free") return;
        const fetchUserCountry = async () => {
            try {
                const response = await fetch("https://ipapi.co/json/");
                const data = await response.json();
                const country = data.country || "US";
                setActionUrl(`${constants.fetchPaymentCreateCheckout}?country=${country}`);
            } catch {
                setActionUrl(`${constants.fetchPaymentCreateCheckout}?country=US`);
            }
        };

        fetchUserCountry();
    }, [auth]);

    if (auth) {
        return (<Modal title={t('auth.account')}>
            <div className="login-modal">
                <div className="login-modal-container">
                    <div className="profile-info">
                        <div className="profile-avatar">
                            {(auth.email || '?')[0].toUpperCase()}
                        </div>
                        <div className="profile-details">
                            <span className="profile-email">{auth.email}</span>
                            <div className="profile-badges">
                                {(Array.isArray(auth.access) ? auth.access : [auth.access || 'free']).map((tag) => (
                                    <span key={tag} className={`profile-badge ${tag === 'free' ? 'free' : 'premium'}`}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                    {(Array.isArray(auth.access) ? auth.access.every(a => a === 'free') : auth.access === 'free') ? <form action={actionUrl} method="POST">
                        <input type="hidden" id="email" name="email" value={auth.email}></input>
                        <input type="hidden" id="topic" name="topic" value={topic}></input>
                        <button type="submit" className="highlight w-100">{t('auth.subscribeNow')}</button>
                    </form> : null}
                    <button className="btn-logout" onClick={handleLogout}>{t('auth.logout')}</button>
                </div>
            </div>
        </Modal>
        )
    }

    if (isPasswordRecovery) {
        return (
            <Modal>
                <div className="login-modal">
                    <div className="login-modal-container">
                        <div className="login-logo">
                            <Logo size="md" />
                        </div>
                        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                            <div className="login-input-group">
                                <input onChange={handleInputChange} name="email" type="email" placeholder={t('auth.email')} />
                            </div>
                            <button type="submit" className="highlight">{getButtonText()}</button>
                        </form>
                        {!isPasswordRecovery ? <>                        <button
                            className="btn-switch"
                            onClick={toggleMode}
                        >
                            {isLogin ? t('auth.noAccount') : t('auth.hasAccount')}
                        </button>
                            <button
                                className="btn-switch"
                                onClick={() => { setIsPasswordRecovery(true) }}
                            >
                                {t('auth.forgotPassword')}
                            </button></> : <button
                                className="btn-switch"
                                onClick={() => { setIsPasswordRecovery(false) }}
                            >
                            {t('common.back')}
                        </button>}
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal>
            <div className="login-modal">
                <div className="login-modal-container">
                    <div className="login-logo">
                        <Logo size="md" />
                    </div>
                    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); } }}>
                        <div className="login-input-group">
                            <input ref={emailRef} onChange={handleInputChange} name="email" type="email" placeholder={t('auth.email')} />
                            <input onChange={handleInputChange} name="password" type="password" placeholder={isLogin ? t('auth.password') : t('auth.createPassword')} />
                        </div>
                        <button type="submit" className="highlight">{getButtonText()}</button>
                        <button type="button" className="highlight secondary" onClick={toggleMode}>
                            {isLogin ? t('auth.register') : t('auth.login')}
                        </button>
                    </form>
                    <button
                        className="btn-switch"
                        onClick={() => { setIsPasswordRecovery(true) }}
                    >
                        {t('auth.forgotPassword')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default LoginModal;
